import React, { useState, useEffect, useContext } from "react";
import {
  Box, Card, CardContent, Grid, Typography, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Chip,
  TextField, Button, Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChatIcon from "@material-ui/icons/Chat";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import TimerIcon from "@material-ui/icons/Timer";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import PeopleIcon from "@material-ui/icons/People";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  RadialBarChart, RadialBar
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, height: "100%", overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: {
    padding: "20px 20px 16px",
    height: "100%"
  },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1, marginBottom: 4 },
  metricSub: { fontSize: 12, marginTop: 4, opacity: 0.8 },
  trendUp: { color: "#38a169", display: "flex", alignItems: "center", gap: 2, fontSize: 12, fontWeight: 600 },
  trendDown: { color: "#e53e3e", display: "flex", alignItems: "center", gap: 2, fontSize: 12, fontWeight: 600 },
  chartCard: {
    borderRadius: 14, backgroundColor: "#fff", padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)"
  },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  effRow: { marginBottom: 20 },
  effHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  effBar: { height: 10, borderRadius: 5, backgroundColor: "#e2e8f0", overflow: "hidden" },
  effFill: { height: "100%", borderRadius: 5, transition: "width 0.8s ease" },
}));

const PERIOD_OPTIONS = [
  { label: "Hoje", value: 0 },
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 15 dias", value: 15 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Últimos 60 dias", value: 60 },
  { label: "Personalizado", value: "custom" },
];

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });

const METRIC_CONFIG = [
  { key: "total",      label: "Total de Conversas", icon: ChatIcon,                color: "#4299e1", bg: "linear-gradient(135deg,#ebf8ff,#bee3f8)", textColor: "#2b6cb0" },
  { key: "finalizados",label: "Finalizadas",         icon: CheckCircleIcon,         color: "#38a169", bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", textColor: "#276749" },
  { key: "atendendo",  label: "Em Atendimento",      icon: AssignmentTurnedInIcon,  color: "#ed8936", bg: "linear-gradient(135deg,#fffaf0,#fbd38d)", textColor: "#c05621" },
  { key: "aguardando", label: "Aguardando",           icon: HourglassEmptyIcon,      color: "#9f7aea", bg: "linear-gradient(135deg,#faf5ff,#e9d8fd)", textColor: "#6b46c1" },
  { key: "avgWait",    label: "Tempo Médio Espera",   icon: TimerIcon,               color: "#e53e3e", bg: "linear-gradient(135deg,#fff5f5,#fed7d7)", textColor: "#c53030" },
  { key: "automacao",  label: "Automação",            icon: PeopleIcon,              color: "#3182ce", bg: "linear-gradient(135deg,#ebf8ff,#90cdf4)", textColor: "#2c5282" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
        <Typography style={{ fontSize: 13, fontWeight: 700, color: "#2d3748" }}>{payload[0]?.name || label}</Typography>
        <Typography style={{ fontSize: 20, fontWeight: 800, color: payload[0]?.fill || "#4299e1" }}>{payload[0]?.value}</Typography>
      </Box>
    );
  }
  return null;
};

export default function ConversasRelatorio() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 7)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    api.get(`/companies/${user?.companyId}`)
      .then(({ data }) => setCompanyName(data?.name || ""))
      .catch(() => {});
  }, [user?.companyId]);

  const isAdmin = user?.profile === "admin" || user?.userType === "administrador" || user?.userType === "gerente";

  const getDateRange = (offset = 0) => {
    if (period === "custom") {
      const diff = (new Date(customEnd) - new Date(customStart)) / 86400000;
      if (offset === 0) return { start: customStart, end: customEnd };
      const prevEnd = fmt(subDays(new Date(customStart), 1));
      const prevStart = fmt(subDays(new Date(customStart), diff + 1));
      return { start: prevStart, end: prevEnd };
    }
    const days = period === 0 ? 1 : period;
    const end = fmt(subDays(new Date(), offset * days));
    const start = fmt(subDays(new Date(), offset * days + (period === 0 ? 0 : days)));
    return { start, end };
  };

  const fetchPeriod = async (start, end) => {
    const p = { pageSize: 1, showAll: isAdmin ? "true" : "false", dateStart: start, dateEnd: end };
    const [automacaoRes, aguardandoRes, atendendoRes, finalizadosRes, dashRes] = await Promise.all([
      api.get("/tickets", { params: { ...p, status: "pending", pendingType: "automation" } }),
      api.get("/tickets", { params: { ...p, status: "pending", pendingType: "assigned" } }),
      api.get("/tickets", { params: { ...p, status: "open" } }),
      api.get("/tickets", { params: { ...p, status: "closed" } }),
      api.get("/dashboard", { params: { dateStart: start, dateEnd: end } }),
    ]);
    return {
      automacao: automacaoRes.data?.count || 0,
      aguardando: aguardandoRes.data?.count || 0,
      atendendo: atendendoRes.data?.count || 0,
      finalizados: finalizadosRes.data?.count || 0,
      avgWaitTime: dashRes.data?.counters?.avgWaitTime || 0,
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const curr = getDateRange(0);
      const prev = getDateRange(1);
      const [currData, prevDataRes] = await Promise.all([
        fetchPeriod(curr.start, curr.end),
        fetchPeriod(prev.start, prev.end),
      ]);
      setData(currData);
      setPrevData(prevDataRes);
    } catch {
      setData(null);
      setPrevData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (period !== "custom") loadData();
    // eslint-disable-next-line
  }, [period]);

  const automacao = data?.automacao || 0;
  const aguardando = data?.aguardando || 0;
  const atendendo = data?.atendendo || 0;
  const finalizados = data?.finalizados || 0;
  const total = automacao + aguardando + atendendo + finalizados;
  const resolutionRate = total > 0 ? Math.round((finalizados / total) * 100) : 0;
  const occupationRate = total > 0 ? Math.round((atendendo / total) * 100) : 0;
  const queueRate = total > 0 ? Math.round((aguardando / total) * 100) : 0;
  const avgWaitRaw = Math.round(data?.avgWaitTime || 0);
  const avgWaitFormatted = avgWaitRaw >= 60
    ? `${Math.floor(avgWaitRaw / 60)}h ${avgWaitRaw % 60}m`
    : `${avgWaitRaw}m`;

  const prevTotal = prevData ? (prevData.automacao + prevData.aguardando + prevData.atendendo + prevData.finalizados) : 0;

  const getTrend = (curr, prev) => {
    if (!prev || prev === 0) return null;
    const diff = Math.round(((curr - prev) / prev) * 100);
    return diff;
  };

  const metricValues = {
    total, finalizados, atendendo, aguardando,
    avgWait: avgWaitFormatted,
    automacao
  };

  const metricPrev = prevData ? {
    total: prevTotal,
    finalizados: prevData.finalizados,
    atendendo: prevData.atendendo,
    aguardando: prevData.aguardando,
    avgWait: null,
    automacao: prevData.automacao
  } : {};

  const metricSubs = {
    total: "Automação + Aguardando + Atendendo + Finalizadas",
    finalizados: `${resolutionRate}% do total`,
    atendendo: "Com atendente atribuído",
    aguardando: "Com fila ou usuário, sem início",
    avgWait: "Desde abertura até 1º atendimento",
    automacao: "Sem fila e sem usuário"
  };

  const pieData = [
    { name: "Finalizadas", value: finalizados, color: "#38a169" },
    { name: "Em Atendimento", value: atendendo, color: "#ed8936" },
    { name: "Aguardando", value: aguardando, color: "#9f7aea" },
    { name: "Automação", value: automacao, color: "#4299e1" },
  ].filter(d => d.value > 0);

  const barData = [
    { name: "Finalizadas", value: finalizados, fill: "#38a169" },
    { name: "Em Atend.", value: atendendo, fill: "#ed8936" },
    { name: "Aguardando", value: aguardando, fill: "#9f7aea" },
    { name: "Automação", value: automacao, fill: "#4299e1" },
  ];

  const efficiencyData = [
    { name: "Resolução", value: resolutionRate, color: "#38a169", desc: `${finalizados} de ${total} conversas finalizadas` },
    { name: "Ocupação", value: occupationRate, color: "#ed8936", desc: `${atendendo} conversas em atendimento` },
    { name: "Fila", value: queueRate, color: "#9f7aea", desc: `${aguardando} conversas aguardando atendente` },
  ];

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "Hoje";
  };

  const handleExportHTML = () => {
    const periodLabel = getPeriodLabel();
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>Relatório de Conversas — ${periodLabel}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;background:#f0f2f5;color:#1a202c;padding:40px}
.header{margin-bottom:32px;border-bottom:3px solid #4299e1;padding-bottom:16px}.header h1{font-size:28px;font-weight:800}
.badge{display:inline-block;background:#4299e1;color:#fff;padding:4px 12px;border-radius:20px;font-size:13px;font-weight:600;margin-top:8px}
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin:24px 0}
.card{border-radius:12px;padding:20px;border:1px solid #e2e8f0;background:#fff}
.card-lbl{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#718096;margin-bottom:8px}
.card-val{font-size:32px;font-weight:800;line-height:1}.card-sub{font-size:12px;color:#a0aec0;margin-top:6px}
.section{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#4a5568;margin:32px 0 12px;border-left:4px solid #4299e1;padding-left:10px}
.eff-row{margin-bottom:20px}.eff-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px}
.eff-lbl{font-size:15px;font-weight:700;color:#2d3748}.eff-pct{padding:3px 10px;border-radius:12px;color:#fff;font-size:13px;font-weight:700}
.eff-bg{height:10px;background:#e2e8f0;border-radius:5px;overflow:hidden}.eff-fill{height:100%;border-radius:5px}
.eff-desc{font-size:12px;color:#a0aec0;margin-top:4px}
.footer{margin-top:40px;border-top:1px solid #e2e8f0;padding-top:16px;font-size:12px;color:#a0aec0}
@media(max-width:768px){.grid{grid-template-columns:1fr 1fr}body{padding:16px}}
@media(max-width:480px){.grid{grid-template-columns:1fr}}</style></head>
<body>
<div class="header"><h1>📊 Relatório de Conversas</h1><p>Análise de atendimentos por período</p><span class="badge">📅 ${periodLabel}</span></div>
<div class="section">Visão Geral</div>
<div class="grid">
  <div class="card" style="border-top:3px solid #4299e1"><div class="card-lbl">Total</div><div class="card-val" style="color:#4299e1">${total}</div><div class="card-sub">Todas as conversas</div></div>
  <div class="card" style="border-top:3px solid #38a169"><div class="card-lbl">Finalizadas</div><div class="card-val" style="color:#38a169">${finalizados}</div><div class="card-sub">${resolutionRate}% do total</div></div>
  <div class="card" style="border-top:3px solid #ed8936"><div class="card-lbl">Em Atendimento</div><div class="card-val" style="color:#ed8936">${atendendo}</div><div class="card-sub">Com atendente atribuído</div></div>
  <div class="card" style="border-top:3px solid #9f7aea"><div class="card-lbl">Aguardando</div><div class="card-val" style="color:#9f7aea">${aguardando}</div><div class="card-sub">Na fila</div></div>
  <div class="card" style="border-top:3px solid #e53e3e"><div class="card-lbl">Tempo Médio Espera</div><div class="card-val" style="color:#e53e3e">${avgWaitFormatted}</div><div class="card-sub">Até 1º atendimento</div></div>
  <div class="card" style="border-top:3px solid #3182ce"><div class="card-lbl">Automação</div><div class="card-val" style="color:#3182ce">${automacao}</div><div class="card-sub">Sem atendente</div></div>
</div>
<div class="section">Eficiência do Atendimento</div>
<div class="card">
${efficiencyData.map(e => `  <div class="eff-row">
    <div class="eff-hdr"><span class="eff-lbl">Taxa de ${e.name}</span><span class="eff-pct" style="background:${e.color}">${e.value}%</span></div>
    <div class="eff-bg"><div class="eff-fill" style="width:${e.value}%;background:${e.color}"></div></div>
    <div class="eff-desc">${e.desc}</div>
  </div>`).join("")}
</div>
<div class="footer">Gerado em ${now} · ${user?.name || "Usuário"} · ${companyName || "Sistema"}</div>
</body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-conversas-${fmt(new Date())}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Conversas</Typography>
          <Typography className={classes.subtitle}>Análise de atendimentos e eficiência por período</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExportHTML}
            disabled={loading || !data}
            style={{ backgroundColor: "#4299e1", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <Button variant="contained" color="primary" size="small" onClick={loadData}
              style={{ textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        {loading && <CircularProgress size={20} />}
        {!loading && data && (
          <Chip label={`Período: ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#ebf8ff", color: "#2b6cb0", fontWeight: 600 }} />
        )}
      </Box>

      {/* KPI Cards */}
      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        {METRIC_CONFIG.map((m) => {
          const Icon = m.icon;
          const val = metricValues[m.key];
          const prevVal = metricPrev[m.key];
          const trend = (m.key !== "avgWait") ? getTrend(Number(val) || 0, Number(prevVal) || 0) : null;
          return (
            <Grid item xs={12} sm={6} md={4} key={m.key}>
              <Card className={classes.metricCard} elevation={0}>
                <Box className={classes.metricInner} style={{ background: m.bg, borderTop: `3px solid ${m.color}` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography className={classes.metricLabel} style={{ color: m.textColor }}>{m.label}</Typography>
                    <Icon style={{ color: m.color, fontSize: 22, opacity: 0.8 }} />
                  </Box>
                  <Typography className={classes.metricValue} style={{ color: m.textColor }}>
                    {loading ? "—" : val}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                    <Typography className={classes.metricSub} style={{ color: m.textColor }}>{metricSubs[m.key]}</Typography>
                    {trend !== null && !loading && (
                      <Box className={trend >= 0 ? classes.trendUp : classes.trendDown}>
                        {trend >= 0
                          ? <TrendingUpIcon style={{ fontSize: 14 }} />
                          : <TrendingDownIcon style={{ fontSize: 14 }} />}
                        {Math.abs(trend)}%
                      </Box>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Charts Row */}
      <Typography className={classes.sectionLabel}>Distribuição e Desempenho</Typography>
      <Grid container spacing={2}>
        {/* Pie Chart */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Distribuição por Status</Typography>
            {loading || total === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados no período</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={95}
                    dataKey="value" paddingAngle={3} label={({ name, percent }) => `${Math.round(percent * 100)}%`}
                    labelLine={false}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [val, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Volume por Categoria</Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Eficiência */}
      <Typography className={classes.sectionLabel}>Eficiência do Atendimento</Typography>
      <Card elevation={0} className={classes.chartCard}>
        <Grid container spacing={3}>
          {efficiencyData.map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Box className={classes.effRow}>
                <Box className={classes.effHeader}>
                  <Typography style={{ fontSize: 14, fontWeight: 700, color: "#2d3748" }}>Taxa de {item.name}</Typography>
                  <Chip label={loading ? "—" : `${item.value}%`} size="small"
                    style={{ backgroundColor: item.color, color: "#fff", fontWeight: 700, fontSize: 13 }} />
                </Box>
                <Box className={classes.effBar}>
                  <Box className={classes.effFill}
                    style={{ width: loading ? "0%" : `${item.value}%`, backgroundColor: item.color }} />
                </Box>
                <Typography style={{ fontSize: 12, color: "#a0aec0", marginTop: 4 }}>{item.desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {!loading && prevData && (
          <>
            <Divider style={{ margin: "16px 0 12px" }} />
            <Typography style={{ fontSize: 12, color: "#a0aec0" }}>
              📊 Período anterior: {prevTotal} conversas totais — comparativo exibido nas setas acima dos cards
            </Typography>
          </>
        )}
      </Card>
    </Box>
  );
}
